import { useMemo, useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { LogOut, Save, RotateCcw, Upload, Eye, KeyRound, X, Plus, Trash2 } from "lucide-react";
import { useAuth } from "@/auth/AuthContext";
import { useContent, imgSrc } from "@/content/ContentContext";
import LogoMark from "@/components/LogoMark";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const getPath = (obj, path) => path.split(".").reduce((o, k) => (o == null ? o : o[k]), obj);
const setPath = (obj, path, val) => {
  const clone = structuredClone(obj);
  const keys = path.split(".");
  let cur = clone;
  for (let i = 0; i < keys.length - 1; i++) cur = cur[keys[i]];
  cur[keys[keys.length - 1]] = val;
  return clone;
};

const buildSchema = () => {
  const groups = [
    {
      id: "site",
      label: "Site & Menu",
      fields: [
        { path: "site.brandName", label: "Brand name" },
        { path: "site.brandSub", label: "Brand strapline" },
        { path: "site.nav.home", label: "Menu label — Home" },
        { path: "site.nav.about", label: "Menu label — About" },
        { path: "site.nav.services", label: "Menu label — Services" },
        { path: "site.nav.training", label: "Menu label — Training" },
        { path: "site.nav.contact", label: "Menu label — Contact" },
        { path: "site.tagline", label: "Tagline (footer)" },
        { path: "site.ctaPrimary", label: "Primary button label" },
        { path: "site.ctaSecondary", label: "Secondary button label" },
        { path: "site.phone", label: "Phone number (display)" },
        { path: "site.phoneHref", label: "Phone number (international format, e.g. +353...)" },
        { path: "site.email", label: "Email address" },
        { path: "site.domain", label: "Website address (display)" },
        { path: "site.footerBlurb", label: "Footer description", type: "textarea" },
        { path: "site.footerNote", label: "Footer small print" },
      ],
    },
    {
      id: "home",
      label: "Home",
      fields: [
        { path: "home.heroOverline", label: "Hero small heading" },
        { path: "home.heroLine1", label: "Hero headline — line 1" },
        { path: "home.heroEmphasis", label: "Hero headline — gold word (line 2)" },
        { path: "home.heroLine2Suffix", label: "Hero headline — after gold word" },
        { path: "home.heroLine3", label: "Hero headline — line 3" },
        { path: "home.heroIntro", label: "Hero paragraph", type: "textarea" },
        { path: "home.heroImage", label: "Hero background image", type: "image" },
        { path: "home.manifestoOverline", label: "Intro small heading" },
        { path: "home.manifestoText", label: "Intro statement", type: "textarea" },
        { path: "home.manifestoLink", label: "Intro link label" },
        { path: "home.servicesOverline", label: "Services small heading" },
        { path: "home.servicesTitle", label: "Services title" },
        { path: "home.servicesCta", label: "Services button label" },
        ...[0, 1, 2, 3].flatMap((i) => [
          { path: `home.services.${i}.title`, label: `Service ${i + 1} — title` },
          { path: `home.services.${i}.text`, label: `Service ${i + 1} — text`, type: "textarea" },
          { path: `home.services.${i}.image`, label: `Service ${i + 1} — image`, type: "image" },
        ]),
        { path: "home.aboutOverline", label: "About small heading" },
        { path: "home.aboutTitle1", label: "About title — line 1" },
        { path: "home.aboutTitle2", label: "About title — line 2" },
        { path: "home.aboutP1", label: "About paragraph 1", type: "textarea" },
        { path: "home.aboutP2", label: "About paragraph 2", type: "textarea" },
        { path: "home.aboutImage", label: "About image", type: "image" },
        { path: "home.aboutBadge", label: "About image caption" },
        { path: "home.aboutCta", label: "About button label" },
        { path: "home.banner.overline", label: "Bottom banner small heading" },
        { path: "home.banner.title", label: "Bottom banner title" },
        { path: "home.banner.blurb", label: "Bottom banner text", type: "textarea" },
      ],
    },
    {
      id: "about",
      label: "About",
      fields: [
        { path: "about.heroOverline", label: "Hero small heading" },
        { path: "about.heroLines", label: "Hero headline (one line per row)", type: "list" },
        { path: "about.heroIntro", label: "Hero paragraph", type: "textarea" },
        { path: "about.logoImage", label: "Brand logo image", type: "image" },
        { path: "about.logoCaption", label: "Logo caption" },
        ...[0, 1, 2].flatMap((i) => [
          { path: `about.chapters.${i}.title`, label: `Chapter ${i + 1} — title` },
          { path: `about.chapters.${i}.body`, label: `Chapter ${i + 1} — text`, type: "textarea" },
        ]),
        { path: "about.principlesOverline", label: "Principles small heading" },
        { path: "about.principlesTitle1", label: "Principles title — line 1" },
        { path: "about.principlesTitle2", label: "Principles title — line 2" },
        { path: "about.principlesText", label: "Principles paragraph", type: "textarea" },
        { path: "about.principles", label: "Principles (one per row)", type: "list" },
        { path: "about.ctaTitle", label: "Bottom strip title" },
        { path: "about.ctaCta", label: "Bottom strip button label" },
        { path: "about.banner.overline", label: "Banner small heading" },
        { path: "about.banner.title", label: "Banner title" },
        { path: "about.banner.blurb", label: "Banner text", type: "textarea" },
      ],
    },
    {
      id: "services",
      label: "Services",
      fields: [
        { path: "services.heroOverline", label: "Hero small heading" },
        { path: "services.heroLines", label: "Hero headline (one line per row)", type: "list" },
        { path: "services.heroIntro", label: "Hero paragraph", type: "textarea" },
        ...[0, 1, 2, 3].flatMap((i) => [
          { path: `services.chapters.${i}.title`, label: `Service ${i + 1} — title` },
          { path: `services.chapters.${i}.tagline`, label: `Service ${i + 1} — tagline` },
          { path: `services.chapters.${i}.desc`, label: `Service ${i + 1} — longer description (optional)`, type: "textarea" },
          { path: `services.chapters.${i}.image`, label: `Service ${i + 1} — image`, type: "image" },
          { path: `services.chapters.${i}.items`, label: `Service ${i + 1} — bullet points (one per row)`, type: "list" },
        ]),
        { path: "services.processOverline", label: "Process small heading" },
        { path: "services.processTitle", label: "Process title" },
        ...[0, 1, 2].flatMap((i) => [
          { path: `services.steps.${i}.t`, label: `Step ${i + 1} — title` },
          { path: `services.steps.${i}.d`, label: `Step ${i + 1} — text`, type: "textarea" },
        ]),
        { path: "services.banner.overline", label: "Banner small heading" },
        { path: "services.banner.title", label: "Banner title" },
        { path: "services.banner.blurb", label: "Banner text", type: "textarea" },
      ],
    },
    {
      id: "training",
      label: "Training",
      fields: [
        { path: "training.heroOverline", label: "Hero small heading" },
        { path: "training.heroLines", label: "Hero headline (one line per row)", type: "list" },
        { path: "training.heroIntro", label: "Hero paragraph", type: "textarea" },
        { path: "training.formatsOverline", label: "Delivery small heading" },
        { path: "training.formatsTitle", label: "Delivery title" },
        ...[0, 1, 2, 3].flatMap((i) => [
          { path: `training.formats.${i}.t`, label: `Delivery card ${i + 1} — title` },
          { path: `training.formats.${i}.d`, label: `Delivery card ${i + 1} — text`, type: "textarea" },
        ]),
        { path: "training.workshopsOverline", label: "Workshops small heading" },
        { path: "training.workshopsTitle", label: "Workshops title" },
        { path: "training.workshopsText", label: "Workshops paragraph", type: "textarea" },
        { path: "training.workshops", label: "Workshops", type: "objectArray", itemName: "workshop", itemFields: [{ key: "title", label: "Title" }, { key: "d", label: "Description", textarea: true }] },
        { path: "training.banner.overline", label: "Banner small heading" },
        { path: "training.banner.title", label: "Banner title" },
        { path: "training.banner.blurb", label: "Banner text", type: "textarea" },
      ],
    },
    {
      id: "contact",
      label: "Contact",
      fields: [
        { path: "contact.heroOverline", label: "Hero small heading" },
        { path: "contact.heroLines", label: "Hero headline (one line per row)", type: "list" },
        { path: "contact.heroIntro", label: "Hero paragraph", type: "textarea" },
        { path: "contact.phoneTitle", label: "Phone card title" },
        { path: "contact.phoneNote", label: "Phone card note", type: "textarea" },
        { path: "contact.emailTitle", label: "Email card title" },
        { path: "contact.emailNote", label: "Email card note", type: "textarea" },
        ...[0, 1, 2].flatMap((i) => [
          { path: `contact.notes.${i}.t`, label: `Note ${i + 1} — title` },
          { path: `contact.notes.${i}.d`, label: `Note ${i + 1} — text`, type: "textarea" },
        ]),
        { path: "contact.quoteOverline", label: "Quote small heading" },
        { path: "contact.quote", label: "Quote" },
        { path: "contact.quoteText", label: "Closing paragraph", type: "textarea" },
      ],
    },
  ];
  return groups;
};

const ImageField = ({ value, onChange, path }) => {
  const inputRef = useRef(null);
  const [busy, setBusy] = useState(false);

  const upload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setBusy(true);
    try {
      const form = new FormData();
      form.append("file", file);
      const { data } = await axios.post(`${API}/upload`, form, { withCredentials: true });
      onChange(data.url);
      toast.success("Image uploaded — remember to Save changes");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Upload failed");
    } finally {
      setBusy(false);
      e.target.value = "";
    }
  };

  return (
    <div>
      <div className="border border-line bg-white overflow-hidden mb-2">
        <img src={imgSrc(value)} alt="" className="w-full h-36 object-cover" />
      </div>
      <input ref={inputRef} type="file" accept="image/*" className="hidden" onChange={upload} data-testid={`upload-${path}`} />
      <button
        type="button"
        onClick={() => inputRef.current?.click()}
        disabled={busy}
        data-testid={`upload-btn-${path}`}
        className="inline-flex items-center gap-2 rounded-full border border-pine/25 px-4 py-2 text-xs font-semibold text-pine transition-colors duration-300 hover:bg-pine hover:text-cream disabled:opacity-60"
      >
        <Upload size={13} />
        {busy ? "Uploading..." : "Upload new image"}
      </button>
    </div>
  );
};

const ObjectArrayEditor = ({ value = [], onChange, itemFields, itemName, path }) => {
  const updateItem = (i, key, v) => onChange(value.map((it, idx) => (idx === i ? { ...it, [key]: v } : it)));
  const removeItem = (i) => onChange(value.filter((_, idx) => idx !== i));
  const addItem = () => onChange([...value, Object.fromEntries(itemFields.map((f) => [f.key, ""]))]);

  return (
    <div className="space-y-4">
      {value.map((item, i) => (
        <div key={i} className="border border-line bg-white p-5" data-testid={`item-${path}-${i}`}>
          <div className="flex items-center justify-between mb-4">
            <span className="font-serif text-lg text-gold-muted font-medium">{String(i + 1).padStart(2, "0")}</span>
            <button
              type="button"
              onClick={() => removeItem(i)}
              data-testid={`remove-${path}-${i}`}
              className="inline-flex items-center gap-1.5 rounded-full border border-red-200 px-3 py-1.5 text-xs font-semibold text-red-700 transition-colors duration-300 hover:bg-red-50"
            >
              <Trash2 size={12} /> Remove
            </button>
          </div>
          <div className="space-y-3">
            {itemFields.map((f) => (
              <div key={f.key}>
                <label className="block text-xs font-semibold tracking-[0.18em] uppercase text-pine mb-1.5">
                  {f.label}
                </label>
                {f.textarea ? (
                  <textarea
                    rows={3}
                    value={item[f.key] ?? ""}
                    onChange={(e) => updateItem(i, f.key, e.target.value)}
                    data-testid={`${path}-${i}-${f.key}`}
                    className="w-full border border-line bg-white px-4 py-3 text-sm text-ink leading-relaxed outline-none focus:border-gold transition-colors duration-300"
                  />
                ) : (
                  <input
                    type="text"
                    value={item[f.key] ?? ""}
                    onChange={(e) => updateItem(i, f.key, e.target.value)}
                    data-testid={`${path}-${i}-${f.key}`}
                    className="w-full border border-line bg-white px-4 py-3 text-sm text-ink outline-none focus:border-gold transition-colors duration-300"
                  />
                )}
              </div>
            ))}
          </div>
        </div>
      ))}
      <button
        type="button"
        onClick={addItem}
        data-testid={`add-${path}`}
        className="inline-flex items-center gap-2 rounded-full bg-pine px-5 py-2.5 text-xs font-semibold text-cream transition-colors duration-300 hover:bg-gold hover:text-pine"
      >
        <Plus size={14} /> Add {itemName}
      </button>
    </div>
  );
};

const PasswordModal = ({ onClose }) => {
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (next !== confirm) {
      setError("New passwords do not match");
      return;
    }
    setBusy(true);
    try {
      await axios.post(
        `${API}/auth/change-password`,
        { current_password: current, new_password: next },
        { withCredentials: true }
      );
      toast.success("Password changed — use it next time you sign in");
      onClose();
    } catch (err) {
      setError(err.response?.data?.detail || "Could not change password");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[70] flex items-center justify-center px-5" data-testid="password-modal">
      <div className="absolute inset-0 bg-pine/70 backdrop-blur-sm" onClick={onClose} />
      <div className="relative w-full max-w-md bg-cream border border-line p-8">
        <div className="flex items-center justify-between mb-6">
          <h2 className="font-serif text-2xl font-medium text-pine">Change password</h2>
          <button type="button" onClick={onClose} aria-label="Close" data-testid="password-modal-close" className="text-ink/50 hover:text-pine">
            <X size={20} />
          </button>
        </div>
        <form onSubmit={submit} className="space-y-4" data-testid="password-change-form">
          <div>
            <label htmlFor="pw-current" className="block text-xs font-semibold tracking-[0.18em] uppercase text-pine mb-2">Current password</label>
            <input id="pw-current" type="password" required value={current} onChange={(e) => setCurrent(e.target.value)} data-testid="pw-current-input" autoComplete="current-password" className="w-full border border-line bg-white px-4 py-3 text-sm outline-none focus:border-gold transition-colors duration-300" />
          </div>
          <div>
            <label htmlFor="pw-new" className="block text-xs font-semibold tracking-[0.18em] uppercase text-pine mb-2">New password (min 8 characters)</label>
            <input id="pw-new" type="password" required minLength={8} value={next} onChange={(e) => setNext(e.target.value)} data-testid="pw-new-input" autoComplete="new-password" className="w-full border border-line bg-white px-4 py-3 text-sm outline-none focus:border-gold transition-colors duration-300" />
          </div>
          <div>
            <label htmlFor="pw-confirm" className="block text-xs font-semibold tracking-[0.18em] uppercase text-pine mb-2">Confirm new password</label>
            <input id="pw-confirm" type="password" required minLength={8} value={confirm} onChange={(e) => setConfirm(e.target.value)} data-testid="pw-confirm-input" autoComplete="new-password" className="w-full border border-line bg-white px-4 py-3 text-sm outline-none focus:border-gold transition-colors duration-300" />
          </div>
          {error && <p className="text-sm text-red-700 bg-red-50 border border-red-200 px-4 py-3" data-testid="password-change-error">{error}</p>}
          <button type="submit" disabled={busy} data-testid="password-change-submit" className="w-full inline-flex items-center justify-center gap-2 rounded-full bg-pine px-6 py-3.5 text-sm font-semibold text-cream transition-colors duration-300 hover:bg-gold hover:text-pine disabled:opacity-60">
            <KeyRound size={15} /> {busy ? "Updating..." : "Update password"}
          </button>
        </form>
      </div>
    </div>
  );
};

const Admin = () => {
  const { user, logout } = useAuth();
  const { content, refresh } = useContent();
  const schema = useMemo(buildSchema, []);
  const [tab, setTab] = useState("site");
  const [draft, setDraft] = useState(null);
  const [saving, setSaving] = useState(false);
  const [showPw, setShowPw] = useState(false);

  const current = draft ?? content;
  const dirty = draft !== null;

  const update = (path, val) => setDraft((d) => setPath(d ?? content, path, val));

  const save = async () => {
    if (!dirty) return;
    setSaving(true);
    try {
      await axios.put(`${API}/content`, { data: draft }, { withCredentials: true });
      await refresh();
      setDraft(null);
      toast.success("Changes saved — live on the site now");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const resetAll = async () => {
    if (!window.confirm("Reset ALL website content back to the original defaults? This cannot be undone.")) return;
    setSaving(true);
    try {
      await axios.delete(`${API}/content`, { withCredentials: true });
      await refresh();
      setDraft(null);
      toast.success("Content reset to defaults");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Reset failed");
    } finally {
      setSaving(false);
    }
  };

  const group = schema.find((g) => g.id === tab);

  return (
    <div className="min-h-screen bg-cream" data-testid="admin-panel">
      <header className="sticky top-0 z-40 bg-pine text-cream grain">
        <div className="relative z-10 mx-auto max-w-6xl px-5 sm:px-8 h-20 flex items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <LogoMark size={38} dark />
            <div>
              <p className="font-serif text-base font-semibold leading-tight">Site Admin</p>
              <p className="text-[10px] tracking-[0.25em] uppercase text-gold">{user?.email}</p>
            </div>
          </div>
          <div className="flex items-center gap-2 sm:gap-3">
            <a
              href="/"
              target="_blank"
              rel="noreferrer"
              data-testid="admin-preview-link"
              className="hidden sm:inline-flex items-center gap-2 rounded-full border border-cream/25 px-4 py-2 text-xs font-semibold text-cream hover:text-gold hover:border-gold transition-colors duration-300"
            >
              <Eye size={13} /> View site
            </a>
            <button
              type="button"
              onClick={resetAll}
              data-testid="admin-reset-button"
              className="inline-flex items-center gap-2 rounded-full border border-cream/25 px-4 py-2 text-xs font-semibold text-cream hover:text-gold hover:border-gold transition-colors duration-300"
            >
              <RotateCcw size={13} /> Reset
            </button>
            <button
              type="button"
              onClick={save}
              disabled={!dirty || saving}
              data-testid="admin-save-button"
              className="inline-flex items-center gap-2 rounded-full bg-gold px-5 py-2 text-xs font-semibold text-pine transition-colors duration-300 hover:bg-cream disabled:opacity-50"
            >
              <Save size={13} /> {saving ? "Saving..." : dirty ? "Save changes" : "Saved"}
            </button>
            <button
              type="button"
              onClick={() => setShowPw(true)}
              data-testid="admin-password-button"
              className="inline-flex items-center gap-2 rounded-full border border-cream/25 px-4 py-2 text-xs font-semibold text-cream hover:text-gold hover:border-gold transition-colors duration-300"
            >
              <KeyRound size={13} /> <span className="hidden sm:inline">Password</span>
            </button>
            <button
              type="button"
              onClick={logout}
              data-testid="admin-logout-button"
              aria-label="Sign out"
              className="inline-flex items-center justify-center w-9 h-9 rounded-full border border-cream/25 text-cream hover:text-gold hover:border-gold transition-colors duration-300"
            >
              <LogOut size={14} />
            </button>
          </div>
        </div>
      </header>

      <div className="mx-auto max-w-6xl px-5 sm:px-8 py-10">
        <p className="text-sm text-ink/60 mb-8 max-w-2xl">
          Edit any wording, menu label or image below, then press <strong>Save changes</strong>. Your updates appear on the
          live site immediately. Nothing here can break the design.
        </p>

        <div className="flex flex-wrap gap-2 mb-10" role="tablist" aria-label="Content sections">
          {schema.map((g) => (
            <button
              key={g.id}
              type="button"
              role="tab"
              aria-selected={tab === g.id}
              onClick={() => setTab(g.id)}
              data-testid={`admin-tab-${g.id}`}
              className={`rounded-full px-5 py-2.5 text-sm font-semibold transition-colors duration-300 ${
                tab === g.id ? "bg-pine text-cream" : "border border-line bg-white text-ink/70 hover:text-pine hover:border-pine/40"
              }`}
            >
              {g.label}
            </button>
          ))}
        </div>

        <div className="grid gap-6 md:grid-cols-2">
          {group.fields.map((f) => {
            const value = getPath(current, f.path);
            const tid = `field-${f.path.replace(/\./g, "-")}`;
            return (
              <div key={f.path} className={f.type === "textarea" || f.type === "list" || f.type === "image" ? "md:col-span-2" : ""}>
                <label className="block text-xs font-semibold tracking-[0.18em] uppercase text-pine mb-2" htmlFor={tid}>
                  {f.label}
                </label>
                {f.type === "image" ? (
                  <ImageField value={value} onChange={(v) => update(f.path, v)} path={f.path.replace(/\./g, "-")} />
                ) : f.type === "objectArray" ? (
                  <ObjectArrayEditor
                    value={value || []}
                    onChange={(v) => update(f.path, v)}
                    itemFields={f.itemFields}
                    itemName={f.itemName}
                    path={f.path.replace(/\./g, "-")}
                  />
                ) : f.type === "textarea" ? (
                  <textarea
                    id={tid}
                    rows={4}
                    value={value ?? ""}
                    onChange={(e) => update(f.path, e.target.value)}
                    data-testid={tid}
                    className="w-full border border-line bg-white px-4 py-3 text-sm text-ink leading-relaxed outline-none focus:border-gold transition-colors duration-300"
                  />
                ) : f.type === "list" ? (
                  <textarea
                    id={tid}
                    rows={Math.max(3, (value || []).length + 1)}
                    value={(value || []).join("\n")}
                    onChange={(e) => update(f.path, e.target.value.split("\n").filter((l) => l.trim() !== ""))}
                    data-testid={tid}
                    className="w-full border border-line bg-white px-4 py-3 text-sm text-ink leading-relaxed outline-none focus:border-gold transition-colors duration-300"
                  />
                ) : (
                  <input
                    id={tid}
                    type="text"
                    value={value ?? ""}
                    onChange={(e) => update(f.path, e.target.value)}
                    data-testid={tid}
                    className="w-full border border-line bg-white px-4 py-3 text-sm text-ink outline-none focus:border-gold transition-colors duration-300"
                  />
                )}
              </div>
            );
          })}
        </div>
      </div>
      {showPw && <PasswordModal onClose={() => setShowPw(false)} />}
    </div>
  );
};

export default Admin;
